/*
  Full BMW Audio Upgrade Package AJAX controller

  Commercial model: package baseline + positive-price upgrades + optional amplifier accessories.
  Product catalog is split into Base & Hi-Fi and Harman Kardon products, so no HK routing
  question is handled by this script.
*/

(function () {
  'use strict';

  const SELECTORS = {
    builder: '[data-bmw-builder][data-builder-kind="full-bmw-audio-upgrade"]',
    submit: '[data-bmw-submit]',
    error: '[data-bmw-error]',
    total: '[data-bmw-total]',
    selectedOptions: '[data-bmw-selected-options]',
    permalink: '[data-bmw-permalink]',
    permalinkUrl: '[data-bmw-permalink-url]',
    permalinkCopy: '[data-bmw-copy-link]',
    vin: '[data-bmw-vin]',
    optionInput: '[data-bmw-option] input[type="radio"]',
    accessorySlot: '[data-bmw-accessory-slot]',
    accessoryGroup: '[data-accessory-group]',
    accessoryEmpty: '[data-accessory-empty]',
    accessoryInput: '[data-bmw-accessory] input[type="checkbox"]',
    centerSlot: '[data-bmw-center-speaker-slot]',
    centerInput: '[data-bmw-center-speaker] input[type="radio"]',
    centerYesInput: '[data-bmw-center-speaker] input[value="yes"]',
    centerPrice: '[data-bmw-center-speaker-price]',
    productForm: 'product-form form',
  };

  const BMW_BUILDER_ADJUSTMENT_VARIANTS = Object.freeze({"1000":"58315288904054","3999":"58315288936822","5500":"58315288969590","9500":"58315289002358","10500":"58315289035126","11000":"58315289067894","13999":"58315289100662","15799":"58315289133430","16999":"58315289166198","18399":"58315289198966","19901":"58315289231734","19999":"58315990270326","20000":"58315289264502","20901":"58315289297270","20950":"58315289330038","20999":"58315289362806","21999":"58315289395574","22450":"58316008489334","23901":"58315990335862","36900":"58315289428342","41900":"58315289461110","42500":"58315289493878","44301":"58315289526646","57450":"58315289559414","62901":"58315289592182","79900":"58315289624950","92900":"58315289657718","93901":"58315289690486","114900":"58315289723254"});

  const BMW_BUILDER_BASE_CONFIG_VARIANTS = Object.freeze({"-16999":"58315468210550","-20999":"58315468243318","-29499":"58315468276086","-37998":"58315468308854","-46498":"58315468341622","-50498":"58315468374390","-67497":"58315468407158"});

  function baseVariantIdFor(builder, options) {
    const reduction = options.reduce((sum, option) => sum + (option.deltaCents < 0 ? option.deltaCents : 0), 0);
    if (!reduction) return String(builder.dataset.baseVariantId || '');
    const variantId = BMW_BUILDER_BASE_CONFIG_VARIANTS[String(reduction)] || '';
    if (!variantId) throw new Error(`BMW builder base configuration is missing for reduction £${(Math.abs(reduction) / 100).toFixed(2)}.`);
    return variantId;
  }

  function adjustmentVariantIdFor(amountCents) {
    return BMW_BUILDER_ADJUSTMENT_VARIANTS[String(Math.round(Number(amountCents || 0)))] || '';
  }

  function formatMoney(cents, currency) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(window.Shopify?.locale || 'en-GB', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'GBP',
      }).format(amount);
    } catch (error) {
      return `£${amount.toFixed(2)}`;
    }
  }

  function normalizeVin(value) {
    return String(value || '').trim().toUpperCase().replace(/\s+/g, '');
  }

  function vinIsValidOrBlank(value) {
    const vin = normalizeVin(value);
    return vin === '' || /^[A-HJ-NPR-Z0-9]{17}$/.test(vin);
  }

  function selectedOptions(builder) {
    return Array.from(builder.querySelectorAll(SELECTORS.optionInput)).filter(input => input.checked && !input.disabled);
  }

  function optionData(input) {
    const liveDelta = input.dataset.liveUpgradePrice || input.dataset.optionLiveUpgradePrice || '';
    const deltaCents = parseInt(liveDelta || input.dataset.optionDeltaCents || '0', 10) || 0;
    return {
      slot: input.dataset.optionSlot || '',
      title: input.dataset.optionTitle || input.value || '',
      sku: input.dataset.optionSku || '',
      deltaCents,
      liveBaselinePriceCents: parseInt(input.dataset.liveBaselinePrice || '0', 10) || 0,
      liveUpgradeProductPriceCents: parseInt(input.dataset.liveUpgradeProductPrice || '0', 10) || 0,
      priceCents: parseInt(input.dataset.optionPriceCents || input.dataset.liveUpgradeProductPrice || input.dataset.liveBaselinePrice || '0', 10) || 0,
      variantId: ({
        'M118007-HIFI-PACKAGE': '49468132065583',
        'M118007-RAM-PACKAGE': '58311210959222',
      })[input.dataset.optionSku || ''] || input.dataset.optionVariantId || '',
      productHandle: input.dataset.optionProductHandle || '',
      classification: input.dataset.optionClassification || 'baseline',
      note: input.dataset.optionNote || '',
      value: input.value || '',
      ampBrand: String(input.dataset.optionAmpBrand || '').toLowerCase(),
      dynamicRearOption: input.dataset.dynamicRearOption || '',
    };
  }

  function selectedAmp(builder) {
    return selectedOptions(builder)
      .map(optionData)
      .find(option => option.slot === 'dsp_amplifier_upgrade');
  }

  function selectedAmpBrand(builder) {
    return (selectedAmp(builder)?.ampBrand || 'musway').toLowerCase();
  }

  function ampSupportsCenterSpeaker(option) {
    if (!option) return false;
    const haystack = `${option.title} ${option.sku} ${option.productHandle} ${option.value}`.toLowerCase();
    return /(up\s*8|up\s*10|ve\s*900\.?7|ve\s*1300\.?11|\b7\s*channel|\b8\s*channel|\b10\s*channel|\b11\s*channel)/i.test(haystack);
  }

  function selectedFrontSpeaker(builder) {
    const input = checkedInputForSlot(builder, 'front_components');
    return input ? optionData(input) : null;
  }

  function parseDynamicMap(input) {
    try {
      return JSON.parse(input.dataset.dynamicMap || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  function findDynamicRearRow(map, front, relationKind) {
    if (!map || !front) return null;
    const frontToken = normalizeToken(front.productHandle);
    const premiumIrToken = 'direct-fit-bmw-premium-4-100-mm-2-way-point-source-coaxial-speaker-set';
    const premiumIrLegacyToken = 'direct-fit-bmw-premium-4-100-mm-2-way-component-speaker-set';
    const aliases = frontToken === premiumIrToken ? [premiumIrLegacyToken] : [];
    const candidates = [front.productHandle, front.sku, front.title, ...aliases].filter(Boolean).map(normalizeToken);
    const match = Object.entries(map).find(([key]) => candidates.includes(normalizeToken(key)));
    return match ? match[1] : null;
  }

  function updateDynamicRearSpeakers(builder) {
    const front = selectedFrontSpeaker(builder);
    const frontHandle = front?.productHandle || '';
    const currency = builder.dataset.currency || 'GBP';
    const dynamicInputs = Array.from(builder.querySelectorAll('[data-dynamic-rear-option]')).filter(input => input.dataset.dynamicRearOption);

    dynamicInputs.forEach(input => {
      const map = parseDynamicMap(input);
      const row = findDynamicRearRow(map, front, input.dataset.dynamicRearOption);
      const label = input.closest('[data-bmw-option]');
      if (!row) {
        input.checked = false;
        input.disabled = true;
        if (label) { label.hidden = true; label.dataset.dynamicMappingMissing = 'true'; }
        return;
      }

      input.disabled = false;
      if (label) { label.hidden = false; delete label.dataset.dynamicMappingMissing; }
      const priceCents = Math.round(parseFloat(row.price || 0) * 100);
      const isComponentsOption = /component/i.test(String(input.dataset.dynamicRearOption || ''));
      const prefix = isComponentsOption ? 'Yes — components with separate tweeter' : 'Yes — coaxials with built-in tweeter';
      const title = [prefix, row.title || front?.title || ''].filter(Boolean).join(': ');

      input.dataset.optionTitle = title;
      input.dataset.optionSku = row.sku || '';
      input.dataset.optionDeltaCents = String(priceCents);
      input.dataset.liveUpgradePrice = String(priceCents);
      input.dataset.liveUpgradeProductPrice = String(priceCents);
      input.dataset.optionPriceCents = String(priceCents);
      input.dataset.optionVariantId = row.variant_id || '';
      input.dataset.optionProductHandle = row.handle || frontHandle;
      input.dataset.optionClassification = 'upgrade';

      if (label) {
        label.dataset.liveUpgradePrice = String(priceCents);
        label.dataset.liveUpgradeProductPrice = String(priceCents);
        label.dataset.deltaCents = String(priceCents);
        label.dataset.productHandle = row.handle || frontHandle;
        const titleEl = label.querySelector('.bmw-builder-option__main strong');
        if (titleEl) titleEl.textContent = title;
        const priceEl = label.querySelector('.bmw-builder-option__price');
        if (priceEl) priceEl.textContent = priceCents > 0 ? '+' + formatMoney(priceCents, currency) : 'Included';
        const details = label.querySelector('.bmw-builder-option__details');
        if (details && row.handle) details.href = '/products/' + row.handle;
        const img = label.querySelector('.bmw-builder-option__media img');
        if (img && row.image_url) img.src = row.image_url;
      }
    });
  }
  function centerSpeakerPriceCents(builder) {
    const front = selectedFrontSpeaker(builder);
    return Math.round((front?.priceCents || 0) * 0.5);
  }

  function selectedCenterSpeaker(builder) {
    const yes = builder.querySelector(SELECTORS.centerYesInput);
    const slot = builder.querySelector(SELECTORS.centerSlot);
    if (!yes || !yes.checked || yes.disabled || !slot || slot.hidden) return null;
    const front = selectedFrontSpeaker(builder);
    const priceCents = centerSpeakerPriceCents(builder);
    return {
      title: front?.title || 'selected front speakers',
      priceCents,
    };
  }

  function accessoryData(input) {
    const priceCents = parseInt(input.dataset.accessoryPriceCents || '0', 10) || 0;
    return {
      title: input.dataset.accessoryTitle || input.value || '',
      sku: input.dataset.accessorySku || input.value || '',
      priceCents,
      variantId: input.dataset.accessoryVariantId || '',
      productHandle: input.dataset.accessoryProductHandle || '',
      brand: String(input.closest('[data-accessory-brand]')?.dataset.accessoryBrand || '').toLowerCase(),
      value: input.value || '',
    };
  }

  function selectedAccessories(builder) {
    return Array.from(builder.querySelectorAll(SELECTORS.accessoryInput))
      .filter(input => input.checked && !input.disabled && !input.closest('[data-accessory-group]')?.hidden)
      .map(accessoryData);
  }

  function toggleCenterSpeaker(builder) {
    const slot = builder.querySelector(SELECTORS.centerSlot);
    if (!slot) return;

    const supported = ampSupportsCenterSpeaker(selectedAmp(builder));
    const yes = slot.querySelector('input[value="yes"]');
    const no = slot.querySelector('input[value="no"]');
    const priceCents = centerSpeakerPriceCents(builder);
    const front = selectedFrontSpeaker(builder);
    const currency = builder.dataset.currency || 'GBP';

    slot.hidden = !supported;
    slot.classList.toggle('is-hidden-for-amp', !supported);
    slot.querySelectorAll('input[type="radio"]').forEach(input => { input.disabled = !supported; });
    if (!supported) {
      if (yes) yes.checked = false;
      if (no) no.checked = true;
    }

    if (yes) {
      yes.dataset.centerSpeakerPriceCents = String(priceCents);
      yes.dataset.centerSpeakerFrontTitle = front?.title || '';
    }
    const price = slot.querySelector(SELECTORS.centerPrice);
    if (price) price.textContent = priceCents > 0 ? `+${formatMoney(priceCents, currency)}` : '+£0.00';
  }


  function normalizeToken(value) {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function tokenMatches(value, token) {
    const normalizedToken = normalizeToken(token);
    if (!normalizedToken) return false;
    const normalizedValue = normalizeToken(value);
    return normalizedValue === normalizedToken || normalizedValue.includes(normalizedToken);
  }

  function inputMatchesToken(input, token) {
    if (!input || !token) return false;
    const haystack = [
      input.value,
      input.dataset.optionTitle,
      input.dataset.optionSku,
      input.dataset.optionProductHandle,
      input.dataset.optionAmpBrand,
      input.dataset.accessoryTitle,
      input.dataset.accessorySku,
      input.dataset.accessoryProductHandle,
    ].filter(Boolean).join(' ');
    return tokenMatches(haystack, token);
  }

  function parseListParam(params, key) {
    const values = [];
    params.getAll(key).forEach(value => {
      String(value || '').split(',').forEach(part => {
        const token = normalizeToken(part);
        if (token) values.push(token);
      });
    });
    return values;
  }

  function checkedInputForSlot(builder, slotKey) {
    return builder.querySelector(`[data-bmw-slot][data-slot-key="${CSS.escape(slotKey)}"] input[type="radio"]:checked`);
  }

  function selectFirstMatchingSlotOption(builder, slotKey, token) {
    const slot = builder.querySelector(`[data-bmw-slot][data-slot-key="${CSS.escape(slotKey)}"]`);
    if (!slot || !token) return false;
    const inputs = Array.from(slot.querySelectorAll('input[type="radio"]'));
    const match = inputs.find(input => inputMatchesToken(input, token));
    if (match && !match.disabled) {
      match.checked = true;
      return true;
    }
    return false;
  }

  function selectedParamForSlot(input) {
    if (!input) return '';
    const data = optionData(input);
    if (data.value === 'baseline' || data.classification === 'baseline') return '';
    if (data.ampBrand) return data.ampBrand;
    return normalizeToken(data.sku || data.productHandle || data.title);
  }

  function applyQueryParameters(builder) {
    const params = new URLSearchParams(window.location.search || '');
    if (!params.toString()) return;

    const amp = normalizeToken(params.get('amp') || '');
    if (amp) selectFirstMatchingSlotOption(builder, 'dsp_amplifier_upgrade', amp);

    const slotMap = {
      front: 'front_components',
      rear: 'rear_coaxials',
      underseat: 'underseat_subwoofers',
      subs: 'underseat_subwoofers',
      rear_components: 'rear_coaxials',
      rearcomponents: 'rear_coaxials',
    };

    Object.entries(slotMap).forEach(([paramKey, slotKey]) => {
      const token = normalizeToken(params.get(paramKey) || '');
      if (token) selectFirstMatchingSlotOption(builder, slotKey, token);
    });

    const speakerToken = normalizeToken(params.get('speakers') || '');
    if (speakerToken) {
      ['front_components', 'rear_coaxials', 'underseat_subwoofers'].forEach(slotKey => {
        selectFirstMatchingSlotOption(builder, slotKey, speakerToken);
      });
    }

    toggleAccessories(builder);
    parseListParam(params, 'accessory').forEach(token => {
      const input = Array.from(builder.querySelectorAll(SELECTORS.accessoryInput)).find(candidate => inputMatchesToken(candidate, token));
      if (input && !input.disabled && !input.closest('[data-accessory-group]')?.hidden) {
        input.checked = true;
        enforceAccessoryExclusivity(builder, input);
      }
    });

    toggleCenterSpeaker(builder);
    if (normalizeToken(params.get('center') || '') === 'yes') {
      const yes = builder.querySelector(SELECTORS.centerYesInput);
      if (yes && !yes.disabled && !yes.closest(SELECTORS.centerSlot)?.hidden) yes.checked = true;
    }
  }

  function selectedBuildParams(builder) {
    const params = new URLSearchParams();
    const ampInput = checkedInputForSlot(builder, 'dsp_amplifier_upgrade');
    const ampBrand = selectedAmpBrand(builder);
    if (ampInput && ampBrand && ampBrand !== 'musway') params.set('amp', ampBrand);

    const paramSlots = {
      front: 'front_components',
      rear: 'rear_coaxials',
      underseat: 'underseat_subwoofers',
    };
    Object.entries(paramSlots).forEach(([paramKey, slotKey]) => {
      const token = selectedParamForSlot(checkedInputForSlot(builder, slotKey));
      if (token) params.set(paramKey, token);
    });

    const accessories = selectedAccessories(builder).map(accessory => normalizeToken(accessory.sku || accessory.productHandle || accessory.title)).filter(Boolean);
    if (accessories.length) params.set('accessory', accessories.join(','));
    if (selectedCenterSpeaker(builder)) params.set('center', 'yes');
    return params;
  }

  function updateBrowserUrl(builder) {
    if (!window.history || typeof window.history.replaceState !== 'function') return;
    const params = selectedBuildParams(builder);
    const nextUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({ fullBmwAudioUpgrade: true }, '', nextUrl);
  }

  function cartPermalink(builder) {
    const ids = [];
    const options = selectedOptions(builder).map(optionData);
    const baseVariantId = baseVariantIdFor(builder, options);
    if (baseVariantId) ids.push(String(baseVariantId));

    options.forEach(option => {
      const isBaseline = option.classification === 'baseline' || option.value === 'baseline';
      if (!isBaseline && option.deltaCents > 0) {
        const adjustmentVariantId = adjustmentVariantIdFor(option.deltaCents);
        if (adjustmentVariantId) ids.push(String(adjustmentVariantId));
      }
    });

    const centerPermalinkSpeaker = selectedCenterSpeaker(builder);
    if (centerPermalinkSpeaker && centerPermalinkSpeaker.priceCents > 0) {
      const adjustmentVariantId = adjustmentVariantIdFor(centerPermalinkSpeaker.priceCents);
      if (!adjustmentVariantId) throw new Error(`BMW builder adjustment variant is missing for center speaker +£${(centerPermalinkSpeaker.priceCents / 100).toFixed(2)}.`);
      ids.push(String(adjustmentVariantId));
    }

    selectedAccessories(builder).forEach(accessory => {
      if (accessory.variantId) ids.push(String(accessory.variantId));
    });

    return ids.length ? `${window.location.origin}/cart/${ids.map(id => `${id}:1`).join(',')}` : window.location.href;
  }

  function updatePermalink(builder) {
    const link = cartPermalink(builder);
    const anchor = builder.querySelector(SELECTORS.permalinkUrl);
    if (anchor) {
      anchor.href = link;
      anchor.textContent = link.replace(window.location.origin, '');
    }
    const wrapper = builder.querySelector(SELECTORS.permalink);
    if (wrapper) wrapper.dataset.cartPermalink = link;
  }

  async function copyPermalink(builder) {
    const link = cartPermalink(builder);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = link;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      const button = builder.querySelector(SELECTORS.permalinkCopy);
      if (button) {
        const original = button.dataset.originalText || button.textContent;
        button.dataset.originalText = original;
        button.textContent = 'Link copied';
        setTimeout(() => { button.textContent = original; }, 1800);
      }
    } catch (error) {
      setError(builder, 'Could not copy the shareable link. You can copy the cart permalink shown above.');
    }
  }

  function clearAccessoryGroup(group) {
    group.querySelectorAll(SELECTORS.accessoryInput).forEach(input => {
      input.checked = false;
      input.disabled = true;
    });
  }

  function toggleAccessories(builder) {
    const ampBrand = selectedAmpBrand(builder);
    const accessorySlot = builder.querySelector(SELECTORS.accessorySlot);
    const empty = builder.querySelector(SELECTORS.accessoryEmpty);
    if (!accessorySlot) return;

    const groups = Array.from(accessorySlot.querySelectorAll(SELECTORS.accessoryGroup));
    let visibleGroupCount = 0;

    groups.forEach(group => {
      const groupName = String(group.dataset.accessoryGroup || '').toLowerCase();
      const show = ampBrand === groupName && ampBrand !== 'esx';
      group.hidden = !show;
      group.querySelectorAll(SELECTORS.accessoryInput).forEach(input => {
        input.disabled = !show;
        if (!show) input.checked = false;
      });
      if (show) visibleGroupCount += 1;
    });

    const hideSlot = ampBrand === 'esx' || visibleGroupCount === 0;
    accessorySlot.classList.toggle('is-hidden-for-amp', hideSlot);
    accessorySlot.hidden = hideSlot;
    if (empty) empty.hidden = ampBrand !== 'esx';

    if (hideSlot && ampBrand !== 'esx') {
      groups.forEach(clearAccessoryGroup);
    }
  }

  function enforceAccessoryExclusivity(builder, changedInput) {
    if (!changedInput || !changedInput.checked) return;
    const groupName = changedInput.dataset.accessoryExclusiveGroup || '';
    if (!groupName) return;

    builder.querySelectorAll(`${SELECTORS.accessoryInput}[data-accessory-exclusive-group="${CSS.escape(groupName)}"]`).forEach(input => {
      if (input !== changedInput) input.checked = false;
    });
  }

  function updateSelectedClasses(builder) {
    Array.from(builder.querySelectorAll('[data-bmw-option], [data-bmw-accessory], [data-bmw-center-speaker]')).forEach(label => {
      const input = label.querySelector('input[type="radio"], input[type="checkbox"]');
      label.classList.toggle('is-selected', Boolean(input && input.checked && !input.disabled));
    });
  }

  function updateSummary(builder) {
    updateDynamicRearSpeakers(builder);
    toggleAccessories(builder);
    toggleCenterSpeaker(builder);
    updateSelectedClasses(builder);

    const currency = builder.dataset.currency || 'GBP';
    const basePrice = parseInt(builder.dataset.basePriceCents || '0', 10) || 0;
    const options = selectedOptions(builder).map(optionData);
    const accessories = selectedAccessories(builder);
    const centerSpeaker = selectedCenterSpeaker(builder);
    const upgradeTotal = options.reduce((sum, option) => sum + option.deltaCents, 0);
    const accessoryTotal = accessories.reduce((sum, accessory) => sum + accessory.priceCents, 0);
    const centerSpeakerTotal = centerSpeaker ? centerSpeaker.priceCents : 0;
    const total = basePrice + upgradeTotal + accessoryTotal + centerSpeakerTotal;

    const totalEl = builder.querySelector(SELECTORS.total);
    if (totalEl) totalEl.textContent = formatMoney(total, currency);

    const selectedEl = builder.querySelector(SELECTORS.selectedOptions);
    if (!selectedEl) return;

    selectedEl.innerHTML = '';

    options.forEach(option => {
      const row = document.createElement('div');
      row.className = 'bmw-builder-summary__line';

      const name = document.createElement('span');
      name.textContent = option.title;

      const price = document.createElement('strong');
      price.textContent = option.deltaCents > 0
        ? `+${formatMoney(option.deltaCents, currency)}`
        : option.deltaCents < 0
          ? `−${formatMoney(Math.abs(option.deltaCents), currency)}`
          : 'Included';

      row.appendChild(name);
      row.appendChild(price);
      selectedEl.appendChild(row);
    });

    accessories.forEach(accessory => {
      const row = document.createElement('div');
      row.className = 'bmw-builder-summary__line bmw-builder-summary__line--accessory';

      const name = document.createElement('span');
      name.textContent = accessory.title;

      const price = document.createElement('strong');
      price.textContent = `+${formatMoney(accessory.priceCents, currency)}`;

      row.appendChild(name);
      row.appendChild(price);
      selectedEl.appendChild(row);
    });

    if (centerSpeaker) {
      const row = document.createElement('div');
      row.className = 'bmw-builder-summary__line bmw-builder-summary__line--center-speaker';

      const name = document.createElement('span');
      name.textContent = `Center Speaker: Matching ${centerSpeaker.title}`;

      const price = document.createElement('strong');
      price.textContent = `+${formatMoney(centerSpeaker.priceCents, currency)}`;

      row.appendChild(name);
      row.appendChild(price);
      selectedEl.appendChild(row);
    }

    updatePermalink(builder);
    updateBrowserUrl(builder);
  }

  function setError(builder, message) {
    const errorEl = builder.querySelector(SELECTORS.error);
    if (!errorEl) return;
    errorEl.textContent = message || '';
    errorEl.hidden = !message;
  }

  function titleWithModifier(title, cents, currency) {
    const cleanTitle = String(title || '').trim();
    const amount = parseInt(cents || '0', 10) || 0;
    return amount > 0
      ? `${cleanTitle} (+${formatMoney(amount, currency)})`
      : amount < 0
        ? `${cleanTitle} (−${formatMoney(Math.abs(amount), currency)})`
        : `${cleanTitle} (Included)`;
  }

  function slotLabel(slot) {
    const labels = {
      dsp_amplifier_upgrade: 'Selected Amp',
      front_components: 'Selected Front Speakers',
      rear_coaxials: 'Selected Rear Speakers',
      underseat_subwoofers: 'Selected Underseat Subwoofers',
    };
    return labels[slot] || String(slot || 'Selected Option').replace(/_/g, ' ').replace(/\w/g, char => char.toUpperCase());
  }

  function cleanAccessoryTitle(accessory) {
    const title = String(accessory.title || accessory.sku || '').trim();
    return title.replace(/^Musway\s+/i, '').replace(/^Match\s+/i, '').trim();
  }

  function buildLineProperties(builder) {
    const vinInput = builder.querySelector(SELECTORS.vin);
    const vin = normalizeVin(vinInput ? vinInput.value : '');
    const options = selectedOptions(builder).map(optionData);
    const accessories = selectedAccessories(builder);
    const centerSpeaker = selectedCenterSpeaker(builder);
    const currency = builder.dataset.currency || 'GBP';

    const properties = {
      'Package': builder.dataset.packageTitle || 'Full BMW Audio Upgrade Package',
    };

    options.forEach(option => {
      const label = option.classification === 'remove_from_package' ? 'Removed from package' : slotLabel(option.slot);
      properties[label] = titleWithModifier(option.title, option.deltaCents, currency);
    });

    properties['Accessories'] = accessories.length
      ? accessories.map(accessory => `${cleanAccessoryTitle(accessory)} (+${formatMoney(accessory.priceCents, currency)})`).join(', ')
      : 'None selected';

    properties['Center Speaker'] = centerSpeaker
      ? `Matching ${centerSpeaker.title} (+${formatMoney(centerSpeaker.priceCents, currency)})`
      : 'Not selected';

    properties['Vehicle VIN'] = vin || 'To be supplied after order';
    properties['VIN / harness review'] = 'Included before fulfilment';

    // Private/internal attributes remain hidden from customer-facing cart rows while preserving staff traceability.
    properties._bmw_package_handle = builder.dataset.packageHandle || '';
    properties._customer_vin = vin;
    if (centerSpeaker) {
      properties._bmw_center_speaker_amount = (centerSpeaker.priceCents / 100).toFixed(2);
      properties._bmw_center_speaker_front = centerSpeaker.title;
    }

    return properties;
  }

  function buildCartItems(builder) {
    const options = selectedOptions(builder).map(optionData);
    const baseVariantId = baseVariantIdFor(builder, options);
    if (!baseVariantId) throw new Error('Base full BMW audio upgrade variant is missing.');

    const properties = buildLineProperties(builder);
    const accessories = selectedAccessories(builder);

    const items = [{ id: Number(baseVariantId), quantity: 1, properties }];

    options.forEach(option => {
      const isBaseline = option.classification === 'baseline' || option.value === 'baseline';
      const isPaidUpgrade = !isBaseline && option.deltaCents > 0;
      if (!isPaidUpgrade) return;
      const adjustmentVariantId = adjustmentVariantIdFor(option.deltaCents);
      if (!adjustmentVariantId) throw new Error(`BMW builder adjustment variant is missing for +£${(option.deltaCents / 100).toFixed(2)}.`);
      items.push({
        id: Number(adjustmentVariantId),
        quantity: 1,
        properties: {
          _bmw_builder_adjustment: 'true',
          _bmw_parent_base_variant: String(baseVariantId),
          _bmw_slot: option.slot,
          _bmw_upgrade_amount: (option.deltaCents / 100).toFixed(2),
          _bmw_source_variant_id: option.variantId || '',
          _bmw_source_sku: option.sku || '',
          _bmw_source_product_handle: option.productHandle || '',
          'Build item': option.title,
          _customer_vin: properties._customer_vin || '',
        },
      });
    });

    const centerSpeaker = selectedCenterSpeaker(builder);
    if (centerSpeaker && centerSpeaker.priceCents > 0) {
      const adjustmentVariantId = adjustmentVariantIdFor(centerSpeaker.priceCents);
      if (!adjustmentVariantId) throw new Error(`BMW builder adjustment variant is missing for center speaker +£${(centerSpeaker.priceCents / 100).toFixed(2)}.`);
      items.push({
        id: Number(adjustmentVariantId),
        quantity: 1,
        properties: {
          _bmw_builder_adjustment: 'true',
          _bmw_parent_base_variant: String(baseVariantId),
          _bmw_slot: 'center_speaker',
          _bmw_upgrade_amount: (centerSpeaker.priceCents / 100).toFixed(2),
          'Build item': `Matching ${centerSpeaker.title}`,
          _customer_vin: properties._customer_vin || '',
        },
      });
    }

    accessories.forEach(accessory => {
      if (accessory.variantId) {
        items.push({
          id: Number(accessory.variantId),
          quantity: 1,
          properties: {
            _bmw_builder_accessory: 'true',
            _bmw_parent_base_variant: String(baseVariantId),
            _bmw_accessory_sku: accessory.sku,
            _bmw_accessory_amount: (accessory.priceCents / 100).toFixed(2),
            _customer_vin: properties._customer_vin || '',
          },
        });
      }
    });

    return items;
  }

  async function addConfiguredPackage(builder) {
    setError(builder, '');

    const vinInput = builder.querySelector(SELECTORS.vin);
    if (vinInput && !vinIsValidOrBlank(vinInput.value)) {
      vinInput.focus();
      throw new Error('Please enter a valid 17-character VIN or leave it blank for follow-up.');
    }

    const submit = builder.querySelector(SELECTORS.submit);
    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
    }

    try {
      const items = buildCartItems(builder);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        let errorText = `Cart add failed (${response.status}).`;
        try {
          const errorJson = await response.json();
          errorText = errorJson.description || errorJson.message || errorText;
        } catch (error) {}
        throw new Error(errorText);
      }

      await response.json();

      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && typeof cartDrawer.getSectionsToRender === 'function') {
        const sections = cartDrawer.getSectionsToRender().map(section => section.id);
        const cartResponse = await fetch(`/?sections=${sections.join(',')}`);
        const sectionHtml = await cartResponse.json();
        cartDrawer.renderContents({ sections: sectionHtml });
      } else if (cartNotification && typeof cartNotification.renderContents === 'function') {
        window.location.href = window.routes?.cart_url || '/cart';
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }
    } finally {
      if (submit) {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      }
    }
  }

  function interceptExistingProductForm(builder) {
    const selector = builder.dataset.productFormSelector || SELECTORS.productForm;
    if (!selector) return;
    const form = document.querySelector(selector);
    if (!form || form.dataset.fullBmwAudioUpgradeIntercepted === 'true') return;

    form.dataset.fullBmwAudioUpgradeIntercepted = 'true';
    form.addEventListener('submit', async event => {
      event.preventDefault();
      event.stopPropagation();
      try {
        await addConfiguredPackage(builder);
      } catch (error) {
        setError(builder, error.message || 'Could not add configured package to cart.');
      }
    }, true);
  }

  function initBuilder(builder) {
    if (builder.dataset.fullBmwAudioUpgradeReady === 'true') return;
    builder.dataset.fullBmwAudioUpgradeReady = 'true';

    builder.addEventListener('change', event => {
      if (event.target.matches(SELECTORS.accessoryInput)) {
        enforceAccessoryExclusivity(builder, event.target);
      }
      if (event.target.matches(SELECTORS.optionInput) || event.target.matches(SELECTORS.accessoryInput) || event.target.matches(SELECTORS.centerInput)) {
        updateSummary(builder);
      }
    });

    const vinInput = builder.querySelector(SELECTORS.vin);
    if (vinInput) {
      vinInput.addEventListener('input', () => {
        vinInput.value = normalizeVin(vinInput.value);
      });
    }

    const submit = builder.querySelector(SELECTORS.submit);
    if (submit) {
      submit.addEventListener('click', async () => {
        try {
          await addConfiguredPackage(builder);
        } catch (error) {
          setError(builder, error.message || 'Could not add configured package to cart.');
        }
      });
    }

    const copyButton = builder.querySelector(SELECTORS.permalinkCopy);
    if (copyButton) {
      copyButton.addEventListener('click', () => copyPermalink(builder));
    }

    interceptExistingProductForm(builder);
    applyQueryParameters(builder);
    updateSummary(builder);
  }

  function initAll() {
    document.querySelectorAll(SELECTORS.builder).forEach(initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
